import {
  GridItem,
  SelectInputField,
  TextInputField,
} from "@certn/cascadia-react";
import { S3FileInfo } from "@certn/cascadia-react/deprecated/upload/types";
import { useAPIRequest, usePeopleAPI } from "@certn/legacy-api";
import { Field, useField } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Item } from "react-stately";

import { CountrySelect } from "../../../../components/AddressInput";
import {
  ApplicantDocumentUpload,
  convertDocumentToS3FileInfo,
} from "../../../../components/ApplicantDocumentUpload";

type DocumentInfoProps = {
  name: string;
  isCitizen: boolean;
};

export function DocumentInfo({ name, isCitizen }: DocumentInfoProps) {
  const { t } = useTranslation();
  const { applicantId } = useParams();
  const { data: categoryTypes } = usePeopleAPI<
    "/internal/people/document-types/",
    Record<
      "australian_citizen" | "non_australian_citizen",
      { key: string; value: string }[]
    >
  >("/internal/people/document-types/", {
    check: "rtw",
  });
  const [, meta, helpers] = useField(name);
  const [, idMeta] = useField(`${name}.id`);
  const [files, setFiles] = useState<S3FileInfo[]>(
    meta.value?.id ? [convertDocumentToS3FileInfo(meta.value)] : []
  );
  const request = useAPIRequest();

  async function deleteOldDocument() {
    const documentId = files[0]?.document?.document_id;

    if (!documentId) {
      return;
    }

    await request(
      `/internal/people/applicant/${applicantId}/document/${documentId}/delete`,
      {
        method: "POST",
      }
    );
  }

  async function handleFilesChange(files: S3FileInfo[]) {
    if (files.length === 0) {
      try {
        await deleteOldDocument();
      } catch (err) {
        console.error("Unable to delete file:", err);
      }
    }

    setFiles(files);

    helpers.setValue({
      ...meta.value,
      id: files[0]?.document?.document_id ?? "",
    });
  }

  return (
    <>
      <GridItem>
        <Field
          name={`${name}.document_type`}
          as={SelectInputField}
          label={t("Document type")}
          placeholder={t("Please select")}
          isRequired
          onChange={(value: string) => {
            helpers.setValue({
              document_type: value,
              document_number: "",
              document_issuing_country: meta.value.document_issuing_country,
              id: "",
            });
            setFiles([]);
          }}
          testID="australia-right-to-work-document-type-select"
        >
          {!categoryTypes ? (
            <Item key="Loading">{t("Loading...")}</Item>
          ) : (
            categoryTypes[
              isCitizen ? "australian_citizen" : "non_australian_citizen"
            ]?.map(({ key, value }) => <Item key={key}>{value}</Item>)
          )}
        </Field>
      </GridItem>

      {meta.value.document_type && (
        <>
          {!isCitizen && (
            <GridItem>
              <CountrySelect
                name={`${name}.document_issuing_country`}
                label={t("Issuing country")}
                isRequired
                placeholder={t("Select issuing country")}
                onFilter={({ alpha_2 }) => alpha_2 !== "AU"}
                testID="australia-right-to-work-issuing-country-select"
              />
            </GridItem>
          )}

          <GridItem>
            <Field
              as={TextInputField}
              name={`${name}.document_number`}
              label={t("Document number")}
              isRequired
              testID="australia-right-to-work-document-number-input"
            />
          </GridItem>

          <GridItem>
            <ApplicantDocumentUpload
              isRequired
              documentType={meta.value.document_type}
              documentSubtype="right_to_work"
              applicantId={applicantId ?? ""}
              files={files}
              setFiles={handleFilesChange}
              error={idMeta.touched && idMeta.error}
              testID="australia-right-to-work-document-upload-button"
            />
          </GridItem>
        </>
      )}
    </>
  );
}
